import React, { useEffect, useRef, useState } from 'react'
import { useInView } from 'framer-motion'
import useEntryMotion from './useEntryMotion'

const DIGITS = '0123456789'

/**
 * Rolls the digits of a value before settling on the real one.
 *
 * A plain count-up cannot handle the shapes these stats take ("3–6", "24h"),
 * so only digit characters are scrambled and everything else — dashes, units,
 * plus signs — stays put throughout.
 */
const ScrambleValue = ({ value, duration = 900, className }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, amount: 0.6 })
  const animateIn = useEntryMotion('scroll')

  // The placeholder zeros are only ever shown when the roll is actually going
  // to run. Otherwise the real figure is rendered from the first paint —
  // a stat frozen at "0" is worse than no animation.
  const [display, setDisplay] = useState(() =>
    animateIn ? String(value).replace(/\d/g, '0') : String(value)
  )

  useEffect(() => {
    const final = String(value)

    if (!animateIn) {
      setDisplay(final)
      return undefined
    }
    if (!inView) return undefined

    let raf
    const start = performance.now()

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration)
      // Characters lock in left to right as the animation runs.
      const locked = Math.floor(t * t * final.length * 1.4)
      setDisplay(
        final
          .split('')
          .map((char, i) => {
            if (!/\d/.test(char) || i < locked || t === 1) return char
            return DIGITS[Math.floor(Math.random() * DIGITS.length)]
          })
          .join('')
      )
      if (t < 1) raf = requestAnimationFrame(tick)
      else setDisplay(final)
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [inView, value, duration, animateIn])

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  )
}

export default ScrambleValue
